import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CustomerOrderResponse, MindOrderExtras } from '../../../core/models/order.model';
import { CurrencyBRLPipe } from '../../pipes/currency-brl.pipe';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import {
  getOrderDisplayTotal,
  getOrderDisplayUnitPrice,
  getOrderPriceSuffixKind,
  getOrderTotalSuffixKind,
  isFreeOrderPrice,
  MindOrderListPriceUi,
  OrderRecurrencyPriceItem,
  resolveMindOrderListPrice,
  shouldApplyMindOrderPriceRules,
} from '../../../core/utils/order-recurrency-price.util';

export interface StatusIcon {
  class: string;
  color: string;
  label: string;
}

export interface OrderPanel {
  identifier: string;
  uuid: string;
  orderDate: string;
  total: number;
  paymentMethod: string;
  status: 'em_analise' | 'cancelado' | 'completo';
  statusIcon: StatusIcon;
  products: OrderProduct[];
  showDetailsButton?: boolean;
  statusTranslationKey: string;
  /** Só vem da API de listagem; true quando reprovado na análise de crédito. */
  creditAnalysisRejected?: boolean;
  /** Mind promo from listing API (`extras`). */
  extras?: MindOrderExtras | null;
}

export interface OrderProduct {
  name: string;
  parentProductName?: string;
  quantity: number;
  price: number;
  variationUuid?: string;
  parentProductUuid?: string;
  paymentRecurrency?: string | null;
}

@Component({
  selector: 'umkt-order-panel',
  standalone: true,
  imports: [CommonModule, CurrencyBRLPipe, I18nPipe],
  templateUrl: './order-panel.component.html',
  styleUrls: ['./order-panel.component.scss'],
})
export class OrderPanelComponent implements OnInit, OnChanges {
  @Input() orders: OrderPanel[] = [];
  @Input() defaultOpen: number = -1;
  @Input() preserveOpenState: boolean = false;
  
  @Output() viewPlans = new EventEmitter<void>();
  @Output() orderDetails = new EventEmitter<{
    uuid: string;
    creditAnalysisRejected: boolean;
  }>();
  @Output() retryWithCard = new EventEmitter<OrderPanel>();

  openedIndexes: number[] = [];
  private openedIdentifiers: Set<string> = new Set<string>();
  private i18nService = inject(I18nService)

  ngOnInit() {
    this.initializeOpenedPanels();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orders'] && !changes['orders'].firstChange) {
      if (this.preserveOpenState) {
        this.preserveOpenedPanelsState();
      } else {
        this.openedIndexes = [];
        this.openedIdentifiers.clear();
        this.initializeOpenedPanels();
      }
    }
  }

  static getStatusTranslationKey(status: string): string {
    switch (status) {
      case 'em_analise': return 'components.orderPanel.status.analysis';
      case 'cancelado': return 'components.orderPanel.status.cancelled';
      case 'completo': return 'components.orderPanel.status.completed';
      default: return 'components.orderPanel.status.default';
    }
  }

  private preserveOpenedPanelsState() {
    const newOpenedIndexes: number[] = [];
    
    this.orders.forEach((order, index) => {
      if (this.openedIdentifiers.has(order.identifier)) {
        newOpenedIndexes.push(index);
      }
    });

    this.openedIndexes = newOpenedIndexes;
  }

  private initializeOpenedPanels() {
    if (this.defaultOpen >= 0 && this.defaultOpen < this.orders.length) {
      this.openedIndexes = [this.defaultOpen];
      if (this.orders[this.defaultOpen]) {
        this.openedIdentifiers.add(this.orders[this.defaultOpen].identifier);
      }
    }
  }

  togglePanel(index: number) {
    const currentIndex = this.openedIndexes.indexOf(index);
    const order = this.orders[index];
    
    if (currentIndex === -1) {
      this.openedIndexes.push(index);
      if (order) {
        this.openedIdentifiers.add(order.identifier);
      }
    } else {
      this.openedIndexes.splice(currentIndex, 1);
      if (order) {
        this.openedIdentifiers.delete(order.identifier);
      }
    }
  }

  isPanelOpen(index: number): boolean {
    return this.openedIndexes.includes(index);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'em_analise':
        return 'status-warning';
      case 'cancelado':
        return 'status-cancelled';
      case 'completo':
        return 'status-completed';
      default:
        return 'status-default';
    }
  }


  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  getFormattedProductName(product: OrderProduct): string {
    if (product.parentProductName) {
      return `${product.parentProductName} - ${product.name}`;
    }
    return product.name;
  }

  static shouldDisplayOrder(order: CustomerOrderResponse): boolean {
    const normalized = (order.status ?? '')
      .toString()
      .toLowerCase()
      .replace(/-/g, '_');
    return normalized !== 'not_mapped';
  }

  static mapCustomerOrderToOrderPanel(order: CustomerOrderResponse): OrderPanel {
    const safeLineItems = Array.isArray(order.lineItems) ? order.lineItems : [];
    const safeProducts = Array.isArray(order.products) ? order.products : [];
    const isCreditAnalysisRejected = order.creditAnalysisApproved === false;

    let status: 'em_analise' | 'cancelado' | 'completo';
    let statusIcon: StatusIcon;
    let statusTranslationKey: string;

    if (isCreditAnalysisRejected) {
      status = 'cancelado';
      statusIcon = OrderPanelComponent.getStatusIcon('rejected');
      statusTranslationKey = 'components.orderPanel.status.creditAnalysisRejected';
    } else {
      status = OrderPanelComponent.mapOrderStatus(order.status);
      statusIcon = OrderPanelComponent.getStatusIcon(order.status);
      statusTranslationKey = OrderPanelComponent.getStatusTranslationKey(status);
    }

    const products: OrderProduct[] =
      safeLineItems.length > 0
        ? safeLineItems.map((lineItem) => {
            const matchingProduct = safeProducts.find(
              (p) =>
                p.uuid === lineItem.productUuid ||
                (!!lineItem.productSku && p.sku === lineItem.productSku)
            );
            return {
              name: lineItem.productName,
              parentProductName: lineItem.parentProductName || undefined,
              quantity: lineItem.quantity,
              price: lineItem.price,
              variationUuid: lineItem.productUuid || undefined,
              parentProductUuid: lineItem.parentProductUuid || undefined,
              paymentRecurrency:
                lineItem.paymentRecurrency ?? matchingProduct?.paymentRecurrency ?? null,
            };
          })
        : safeProducts.map((p) => ({
            name: p.name,
            parentProductName: p.parentProductName || undefined,
            quantity: p.quantity,
            price: p.price,
            variationUuid: p.uuid || undefined,
            paymentRecurrency: p.paymentRecurrency ?? null,
          }));

    return {
      identifier: order.identifier,
      uuid: order.uuid,
      orderDate: order.createdAt,
      total: order.total,
      paymentMethod: order.paymentMethod,
      status,
      statusIcon,
      statusTranslationKey,
      products,
      showDetailsButton: true,
      creditAnalysisRejected: isCreditAnalysisRejected,
      extras: order.extras ?? null,
    };
  }
  private static mapOrderStatus(apiStatus: string): 'em_analise' | 'cancelado' | 'completo' {
    const normalized = (apiStatus ?? '').toString().toLowerCase();
    switch (normalized) {
      case 'completed':
      case 'partial_completed':
      case 'created':
      case 'success':
      case 'done':
      case 'sucesso':
      case 'approved':
      case 'paid':
      case 'delivered':
        return 'completo';
      case 'canceled':
      case 'cancelled':
      case 'refunded':
      case 'failed':
      case 'not_found':
      case 'bad_request':
      case 'rejected':
        return 'cancelado';
      case 'analysis':
      case 'pending_activation':
      case 'on_hold':
      case 'pending':
      case 'processing':
      case 'analyzing':
      case 'awaiting-cpc':
      case 'awaiting-mind':
      default:
        return 'em_analise';
    }
  }

  private static getStatusIcon(apiStatus: string): StatusIcon {
    const mappedStatus = OrderPanelComponent.mapOrderStatus(apiStatus);
    
    switch (mappedStatus) {
      case 'em_analise':
        return {
          class: 'mdn-Icon-circulo-exclamacao mdn-Icon--sm',
          color: 'var(--color-yellow-600-to-white)',
          label: 'circulo-exclamacao'
        };
      case 'cancelado':
        return {
          class: 'mdn-Icon-circulo-exclamacao mdn-Icon--sm',
          color: 'var(--color-red-300-to-white)',
          label: 'circulo-exclamacao'
        };
      case 'completo':
        return {
          class: 'mdn-Icon-check-circulo mdn-Icon--sm',
          color: 'var(--color-green-200-to-white)',
          label: 'check-circulo'
        };
        default:
        return {
          class: 'mdn-Icon-circulo-exclamacao mdn-Icon--sm',
          color: 'var(--color-cool-gray-50-to-white)',
          label: 'circulo-exclamacao'
        };
    }
  }

  onButtonClick(order: OrderPanel) {
    if (!order.status || !order.identifier) {
      return;
    }
    
    if (order.status === 'completo') {
      this.viewPlans.emit();
    } else {
      this.orderDetails.emit({
        uuid: order.uuid,
        creditAnalysisRejected: order.creditAnalysisRejected === true,
      });
    }
  }

  onRetryWithCardClick(order: OrderPanel, event: Event): void {
    event.stopPropagation();
    if (order.creditAnalysisRejected === true && order.paymentMethod !== 'MIND') {
      this.retryWithCard.emit(order);
    }
  }

  private toRecurrencyItem(product: OrderProduct): OrderRecurrencyPriceItem {
    return {
      price: product.price,
      quantity: product.quantity,
      variationUuid: product.variationUuid,
      productUuid: product.parentProductUuid,
      paymentRecurrency: product.paymentRecurrency,
    };
  }

  getDisplayTotal(order: OrderPanel): number {
    return getOrderDisplayTotal(
      (order.products || []).map((p) => this.toRecurrencyItem(p)),
      order.total || 0,
      order.paymentMethod,
      order.extras
    );
  }

  getTotalSuffix(order: OrderPanel): string {
    const kind = getOrderTotalSuffixKind(
      (order.products || []).map((p) => this.toRecurrencyItem(p)),
      order.paymentMethod,
      order.extras
    );
    if (kind === 'perYear') {
      return this.i18nService.instant('components.orderPanel.perYear');
    }
    if (kind === 'perMonth') {
      return this.i18nService.instant('components.orderPanel.perMonth');
    }
    return '';
  }

  getMindListPrice(
    product: OrderProduct,
    order?: OrderPanel
  ): MindOrderListPriceUi | null {
    if (!order || !shouldApplyMindOrderPriceRules(order.paymentMethod, order.extras)) {
      return null;
    }
    return resolveMindOrderListPrice(product.price, order.extras);
  }

  getMonthlyPrice(product: OrderProduct, order?: OrderPanel): number {
    return getOrderDisplayUnitPrice(
      this.toRecurrencyItem(product),
      order?.paymentMethod,
      order?.extras
    );
  }

  isFreeProductPrice(product: OrderProduct): boolean {
    return isFreeOrderPrice(product.price);
  }

  formatMindOriginalPrice(price: number): string {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  getMindPromoDurationKey(promo: MindOrderListPriceUi): string {
    return promo.promotionMonths === 1
      ? 'components.productCard.promoFirstMonth'
      : 'components.productCard.promoFirstMonths';
  }

  getMonthlySuffix(product: OrderProduct, order?: OrderPanel): string {
    const kind = getOrderPriceSuffixKind(
      this.toRecurrencyItem(product),
      order?.paymentMethod,
      order?.extras
    );
    if (kind === 'perYear') {
      return this.i18nService.instant('components.orderPanel.perYear');
    }
    if (kind === 'perMonth') {
      return this.i18nService.instant('components.orderPanel.perMonth');
    }
    return '';
  }
}
